import { apiGet } from './auth'

/** Grouping values accepted by `GET /api/v1/butlaroo/sales-analysis`. */
export type SalesAnalysisGrouping =
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'quarter'
  | 'weekday'

/** One channel in a point or in the period totals. */
export interface SalesAnalysisChannel {
  key:
    | 'kiosk'
    | 'instore'
    | 'uber_eats'
    | 'thuisbezorgd'
    | 'click_collect'
    | 'catering'
  label: string
  sales_eur: number
  orders: number
  aov_eur: number
  share_pct: number
  /** Positive VAT-exclusive refunded line value. */
  refunds_eur?: number
  refund_count?: number
}

/** One hour/day/week/… bucket. */
export interface SalesAnalysisPoint {
  key: string
  label: string
  detail: string | null
  sales_eur: number
  orders: number
  refunds_eur?: number
  aov_eur: number
  channels: SalesAnalysisChannel[]
  occurrences: number | null
}

export interface SalesAnalysisTotals {
  sales_eur: number
  orders: number
  aov_eur: number
  refunds_eur?: number
  refund_count?: number
}

/** Per-shop fetch outcome; unavailable shops are reported, never zeroed. */
export interface SalesAnalysisStore {
  store_id: number
  store_code: string
  store_name: string
  available: boolean
  business_id: number | null
  timezone: string | null
  currency: string | null
  order_rows: number
  line_rows: number
  pages_fetched: number
  skipped_orders: number
  error_code: string | null
  error_message: string | null
}

/** Response of `GET /api/v1/butlaroo/sales-analysis`. */
export interface SalesAnalysisResponse {
  start_date: string
  end_date: string
  grouping: SalesAnalysisGrouping
  days: number
  currency: string
  timezone: string
  source: 'butlaroo_orders' | 'vmhub_history' | 'mixed'
  aggregation: 'order_lines'
  sales_basis:
    | 'line_unit_price_ex_vat'
    | 'net_sales_ex_vat'
    | 'mixed_net_sales_and_line_unit_price_ex_vat'
  refunds_available: boolean
  channels_available: boolean
  stores: SalesAnalysisStore[]
  points: SalesAnalysisPoint[]
  totals: SalesAnalysisTotals
  channels: SalesAnalysisChannel[]
  unmapped_sales_eur: number
  unmapped_orders: number
  order_rows: number
  line_rows: number
  pages_fetched: number
  warnings: string[]
  fetched_at: string
}

export interface SalesAnalysisParams {
  from: string
  to: string
  /** Omit to let the server use the account's whole entitled scope. */
  storeIds?: number[]
  grouping: SalesAnalysisGrouping
  /** TanStack Query passes this so a superseded fetch aborts. */
  signal?: AbortSignal
}

/**
 * Order-line-backed Sales Analysis. The server enforces capability and store
 * scope; this client only sends the selected dates, shops and grouping.
 */
export function fetchSalesAnalysis({
  from,
  to,
  storeIds,
  grouping,
  signal,
}: SalesAnalysisParams): Promise<SalesAnalysisResponse> {
  return apiGet<SalesAnalysisResponse>(
    '/butlaroo/sales-analysis',
    {
      start_date: from,
      end_date: to,
      store_ids: storeIds && storeIds.length > 0 ? storeIds : undefined,
      grouping,
    },
    signal,
  )
}
